import { PayloadAction } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import i18next from "config/i18next";
import * as transactionsMiddleware from "middleware/transactions";
import { actions as notificationActions } from "reducers/notification";
import { actions } from "reducers/transactions";
import { NotificationStateType } from "reducers/types/notification";
import { GetTransactionsSuccessType } from "reducers/types/transaction";
import {
  call,
  CallEffect,
  ForkEffect,
  put,
  PutEffect,
  takeLatest,
} from "redux-saga/effects";
import { createCSV } from "utils/files";
import { BulkCancelTransactionType } from "./types/transactions";
import {
  FilterTransactionsType,
  TransactionResponseContent,
  TransactionsExportContentType,
} from "./types/transactions";
import { serverErrorAlert } from "utils/alerts";
import { DevolutionUndeliveredResponseType } from "./types/detailTransactions";
import { store } from "store";

const sagas: ForkEffect<never>[] = [
  takeLatest(actions.getTransactionsRequest.type, getTransactions),
  takeLatest(actions.getTransactionsExportRequest.type, getTransactionsExport),
  takeLatest(actions.getMoreTransactionsRequest.type, getMoreTransactions),
  takeLatest(actions.postCancelTransactionsRequest.type, cancelTransactions),
];

export default sagas;

function* getTransactions({
  payload,
}: PayloadAction<FilterTransactionsType>): Generator<
  | CallEffect<AxiosResponse<TransactionResponseContent>>
  | PutEffect<{ type: string; content: NotificationStateType }>
  | PutEffect<{ type: string; transactions: GetTransactionsSuccessType }>
  | PutEffect<{ type: string }>,
  void,
  TransactionResponseContent
> {
  delete payload["date"];
  const response = yield call(transactionsMiddleware.getTransactions, payload);
  if (response.status !== 200) {
    switch (response.data.statusCode) {
      case 20011:
        yield put(
          notificationActions.showNotification({
            id: "notification-range-error",
            level: "error",
            title: i18next.t("transactions:rangeExceededTitle"),
            body: i18next.t("transactions:rangeExceededMessage"),
          })
        );
        yield put(actions.getTransactionsError());
        break;
      case 20013:
        yield put(actions.setExportEnabled());
        yield put(
          actions.getTransactionsSuccess({
            items: [],
            limit: 0,
            offset: 0,
            hasMore: false,
          })
        );
        break;

      default:
        yield put(notificationActions.showNotification(serverErrorAlert()));
        yield put(actions.getTransactionsError());
        break;
    }
    yield put(actions.setExportEnabled());
  } else {
    const {
      result: { items },
      limit,
      offset,
      hasMore,
    } = response.data;
    yield put(
      actions.setExportEnabled(
        payload.pickerId ||
          payload.transactionCode ||
          payload.minSla ||
          payload.tripId ||
          payload.orderNumber
      )
    );
    yield put(
      actions.getTransactionsSuccess({
        items,
        limit,
        offset,
        hasMore,
      })
    );
  }
}

function* getMoreTransactions({
  payload,
}: PayloadAction<FilterTransactionsType>): Generator<
  | CallEffect<AxiosResponse<TransactionResponseContent>>
  | PutEffect<{ type: string }>,
  void,
  TransactionResponseContent
> {
  delete payload["date"];
  const response = yield call(transactionsMiddleware.getTransactions, payload);

  if (response.status !== 200) {
    yield put(actions.getTransactionsError());
  } else {
    const {
      result: { items },
      limit,
      offset,
      hasMore,
    } = response.data;
    yield put(
      actions.getMoreTransactionsSuccess({
        items,
        limit,
        offset,
        hasMore,
      })
    );
  }
}

function* getTransactionsExport({
  payload,
}: PayloadAction<FilterTransactionsType>): Generator<
  | CallEffect<AxiosResponse<TransactionsExportContentType>>
  | PutEffect<{ type: string; payload: NotificationStateType }>
  | PutEffect<{ type: string; payload: undefined }>,
  void,
  TransactionsExportContentType
> {
  const response = yield call(
    transactionsMiddleware.getTransactionsExport,
    payload
  );
  if (response.status !== 200) {
    yield put(actions.getTransactionsExportError());
  } else {
    createCSV(response.data, "transactions");
    yield put(
      notificationActions.showNotification({
        id: "notification-export-success",
        level: "success",
        title: i18next.t("alerts:exportTitle"),
        body: i18next.t("alerts:exportMessage"),
      })
    );
    yield put(actions.getTransactionsExportSuccess());
  }
}

function* cancelTransactions({
  payload,
}: PayloadAction<BulkCancelTransactionType>): Generator<
  | void
  | PutEffect<{ payload: NotificationStateType; type: string }>
  | CallEffect<AxiosResponse<DevolutionUndeliveredResponseType>>
  | PutEffect<{ payload: boolean; type: string }>
  | PutEffect<{ payload: FilterTransactionsType; type: string }>
  | PutEffect<
      { payload: undefined; type: string } | { payload: never[]; type: string }
    >,
  void,
  { error: string; status: number; message: string[] }
> {
  const { filters } = store.getState().transactions;
  const response = yield call(
    transactionsMiddleware.postCancelTransaction,
    payload
  );
  if (response.status !== 204) {
    yield put(actions.postCancelTransactionsError(response));
  } else {
    yield put(actions.postCancelTransactionsSuccess());
    yield put(actions.toggleMasiveCancelationModal(false));
    yield put(
      notificationActions.showNotification({
        level: "success",
        title: i18next.t(
          `${
            payload.transactionsIds.length > 1
              ? "transactions:successCancelationTitle"
              : "transactions:successCancelationTitleSingular"
          }`
        ),
        body: i18next.t(
          `${
            payload.transactionsIds.length > 1
              ? "transactions:successCancelationBody"
              : "transactions:successCancelationBodySingular"
          }`,
          {
            amount: payload.transactionsIds.length,
          }
        ),
      })
    );
    yield put(actions.getTransactionsRequest({ ...filters }));
  }
}
